'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { BedDouble, Layers, Maximize2 } from 'lucide-react'

// StatRow + StatTiles — cards.jsx:133-179. All stat fields are optional:
// cells render only for the values a listing actually has, so surfaces no
// longer need local "partial" mirrors of this band.
export interface StatListing {
  rooms?: number
  area?: number
  floor?: number
  totalFloors?: number
}

const cellCls = 'flex min-w-0 flex-1 items-center justify-center gap-[5px]'
const dividerCls = 'w-px self-stretch bg-outline-variant'

// The tile variant shows the same caption in title case ('ROOMS' → 'Rooms').
const tileCase = (s: string) => (s ? s.charAt(0) + s.slice(1).toLocaleLowerCase() : s)

export interface StatRowProps {
  listing: StatListing
  size?: number
  className?: string
}

// Compact icon band (redesigned StatRow): each present stat renders as an
// icon + value on one row — no gauges or captions. The design's Material
// bed/square_foot/stairs map to the codebase's rooms/area/floor icon trio.
// Values render larger than the design's 12.5px (user-directed): rooms/area/
// floor is core info, so it gets a readable size across every card surface.
export function StatRow({ listing, size = 15, className }: StatRowProps) {
  const { rooms, area, floor, totalFloors } = listing
  const iconSize = size + 2

  const cells: React.ReactNode[] = []
  if (typeof rooms === 'number') {
    cells.push(
      <div key="rooms" className={cellCls} title={`${rooms} ${rooms > 1 ? 'rooms' : 'room'} + kitchenette`}>
        <BedDouble size={iconSize} className="shrink-0 text-ink-muted" />
        <span className="whitespace-nowrap font-bold" style={{ fontSize: size }}>
          {rooms}+kk
        </span>
      </div>
    )
  }
  if (typeof area === 'number') {
    cells.push(
      <div key="area" className={cellCls} title={`${area} m² living area`}>
        <Maximize2 size={iconSize} className="shrink-0 text-ink-muted" />
        <span className="whitespace-nowrap font-bold" style={{ fontSize: size }}>
          {area} m²
        </span>
      </div>
    )
  }
  if (typeof floor === 'number') {
    cells.push(
      <div
        key="floor"
        className={cellCls}
        title={totalFloors ? `Floor ${floor} of ${totalFloors}` : `Floor ${floor}`}
      >
        <Layers size={iconSize} className="shrink-0 text-ink-muted" />
        <span className="whitespace-nowrap font-bold" style={{ fontSize: size }}>
          {floor}
        </span>
      </div>
    )
  }
  if (!cells.length) return null
  return (
    <div className={cn('mt-2 flex items-stretch gap-2 rounded-shape-sm bg-surface-low px-2 py-1.5', className)}>
      {cells.map((cell, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className={dividerCls} />}
          {cell}
        </React.Fragment>
      ))}
    </div>
  )
}

// Stat tiles — the mobile detail's Rooms / Area / Floor containers.
// `floorOverTotal` renders the floor tile as `floor/totalFloors` — kept for
// the detail page's partial-stats surface, which showed the total there.
export function StatTiles({
  listing,
  floorOverTotal = false,
}: {
  listing: StatListing
  floorOverTotal?: boolean
}) {
  const { t } = useI18n()
  const items: Array<[string, string]> = []
  if (typeof listing.rooms === 'number') {
    items.push([`${listing.rooms}+kk`, tileCase(t('listingCard.statRooms', 'ROOMS'))])
  }
  if (typeof listing.area === 'number') {
    items.push([`${listing.area} m²`, tileCase(t('listingCard.statArea', 'AREA'))])
  }
  if (typeof listing.floor === 'number') {
    items.push([
      floorOverTotal && listing.totalFloors
        ? `${listing.floor}/${listing.totalFloors}`
        : `${listing.floor}`,
      tileCase(t('listingCard.statFloor', 'FLOOR')),
    ])
  }
  if (!items.length) return null
  return (
    <div
      className="mt-[10px] grid gap-2"
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map(([val, label]) => (
        <div key={label} className="rounded-shape-sm bg-surface-low px-[14px] py-3">
          <div className="text-[16px] font-bold">{val}</div>
          <div className="mt-[3px] text-[11px] text-ink-muted">{label}</div>
        </div>
      ))}
    </div>
  )
}
